import { NetworkStatus, useLazyQuery, useMutation } from '@apollo/client';
import {
  PrimaryButton,
  SelectionMode,
  Stack,
  Text,
  makeStyles,
} from '@fluentui/react';
import { ActionMessageModal } from 'common/components/ActionMessageModal';
import { cutToast } from 'utility/toast';
import { InfiniteList } from 'common/components/InfiniteList';
import { ColumnData, SearchBar } from 'common/components/SearchBar';
import { TABLE_ROWS } from 'common/constants';
import { useCommonStyles } from 'common/styles';
import { EntityDeleteInput } from 'common/types/globalTypes';
import { EntityType } from 'common/types/utility';
import { getSortedColumns } from 'common/utils/columnUtilities';
import { OrderDirection, SortOrder } from 'common/utils/commonTypes';
import { useCallback, useEffect, useRef, useState } from 'react';

import { LookupAccountFields } from '../__generated__/LookupAccountFields';
import { CreateChartOfAccounts } from '../components/CreateChartOfAccounts';
import { EditChartAccounts } from '../components/EditChartOfAccounts';
import { AccountsEntryModal } from '../view/AccountsEntryModal';
import {
  LookupAccountCoaSearch,
  LookupAccountCoaSearchVariables,
} from './__generated__/lookupAccountCoaSearch';
import {
  LookupAccountDelete,
  LookupAccountDeleteVariables,
} from './__generated__/LookupAccountDelete';
import { ChartAccount } from './chartAccount';
import { ChartOfAccounts_chartOfAccounts_nodes } from './chartAccount/__generated__/ChartOfAccounts';
import { getColumns } from './columns.data';
import { ColumnStylesProps, onRenderItems } from './columns.render';
import { CorporateAccountAssignment } from './CorporateAccountAssignment';
import LOOKUP_ACCOUNTS_COA_SEARCH from './lookupAccountCoaSearch.graphql';
import LOOKUP_ACCOUNTS_DELETE from './LookupAccountDelete.graphql';
import { getTransformedData, onHandleSearch } from './utils';

import { HelpIcon } from 'common/trainingSystem/HelpIcon';

const useStyles = makeStyles(() => ({
  onRenderColumnStack: {
    height: 25,
  },
}));

export type LookupAccountSearchFields = LookupAccountFields & {
  corporateAccountName: string;
};

export const AccountsEntryList = () => {
  const styles = useStyles();
  const commonStyles = useCommonStyles();
  const renderRef = useRef(false);
  const [lookupName, setLookupName] = useState<string | null>(null);
  const [selectedRows, setSelectedRows] = useState<LookupAccountSearchFields[]>(
    []
  );
  const [chartOfAccount, setChartOfAccount] =
    useState<ChartOfAccounts_chartOfAccounts_nodes | null>(null);
  const [chartOfAccountLoading, setChartOfAccountLoading] =
    useState<boolean>(true);
  const [sortOrderParam, setSortOrderParam] = useState<SortOrder>();
  const [gridColumns, setGridColumns] = useState<ColumnData[]>([]);
  const [isOpen, setIsOpen] = useState(false);

  const [getAccounts, accountsQueryInfo] = useLazyQuery<
    LookupAccountCoaSearch,
    LookupAccountCoaSearchVariables
  >(LOOKUP_ACCOUNTS_COA_SEARCH, {
    variables: {
      first: TABLE_ROWS,
      orderBy: [
        'LOOKUP_ACCOUNT_ASC',
        'LOOKUP_NAME_ASC',
        'PROJECT_ACCOUNT_ASC',
        'PRIMARY_KEY_ASC',
      ],
      lookupName: '',
      chartOfAccountId: '',
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-and-network',
  });

  const [deleteCorporateAccounts] = useMutation<
    LookupAccountDelete,
    LookupAccountDeleteVariables
  >(LOOKUP_ACCOUNTS_DELETE, { errorPolicy: 'all' });

  const { loading, data, error, fetchMore, networkStatus, refetch } =
    accountsQueryInfo;

  const handleSearch = async (showMore: boolean) => {
    setSelectedRows([]);
    if (chartOfAccount) {
      const { queryVariables } = onHandleSearch(
        lookupName,
        showMore,
        data,
        chartOfAccount.id,
        sortOrderParam
      );
      if (showMore) fetchMore?.({ variables: queryVariables });
      else getAccounts({ variables: queryVariables });
    }
  };

  const handleSearchMemo = useCallback(handleSearch, [
    sortOrderParam,
    lookupName,
    chartOfAccount?.id,
  ]);

  const _onColumnClick = (clickedColumn: ColumnData) => {
    const { newColumns, desc } = getSortedColumns(clickedColumn, gridColumns);
    setGridColumns(newColumns);
    setSortOrderParam({
      column: clickedColumn.key,
      direction: desc ? OrderDirection.DESC : OrderDirection.ASC,
    });
  };

  const refetching = loading && networkStatus !== NetworkStatus.fetchMore;

  const { nodes, pageInfo } = {
    ...data?.lookupAccountCoaSearch,
  };
  const columnStyles: ColumnStylesProps = {
    onRenderColumnStack: styles.onRenderColumnStack,
  };

  const transformedData = refetching
    ? undefined
    : getTransformedData(refetching, nodes);

  const _renderItemColumn = useCallback(
    onRenderItems(columnStyles, chartOfAccount),
    [columnStyles]
  );

  const setCustomColumns = useCallback(() => {
    setGridColumns(getColumns());
  }, []);

  const _onConfirm = async () => {
    const selectedData: LookupAccountSearchFields[] = selectedRows;
    const deletedRows: LookupAccountSearchFields[] = selectedData.filter(
      (item) => item._isDeletable
    );
    const entityDelete: EntityDeleteInput[] = selectedData
      .filter((item) => item._isDeletable)
      .map((item) => {
        return { id: item.id, rowTimestamp: item._rowTimestamp! };
      });
    const { errors } = await deleteCorporateAccounts({
      variables: {
        input: {
          entityDelete,
        },
      },
      update(cache) {
        deletedRows.forEach((item) => {
          const identity = cache.identify({
            ...item,
          });
          cache.evict({ id: identity });
          cache.gc();
        });
      },
    });
    if (errors?.length) cutToast.error(errors[0].message);
    else {
      cutToast.success('Record deleted successfully.');
    }
  };

  useEffect(() => {
    setCustomColumns();
  }, [setCustomColumns]);

  useEffect(() => {
    if (renderRef.current) handleSearchMemo(false);
    else renderRef.current = true;
  }, [handleSearchMemo]);

  const isDataLoading = loading || (!data && !error);

  return (
    <>
      <Stack
        tokens={{ childrenGap: 20 }}
        className={commonStyles.listHeaderContainer}
      >
        <Stack
          horizontal
          verticalAlign="center"
          horizontalAlign="space-between"
          className={commonStyles.listTitleContainer}
        >
          <Stack horizontal tokens={{ childrenGap: 10 }}>
            <Stack
              horizontal
              verticalAlign="center"
              tokens={{ childrenGap: 5 }}
              style={{ paddingRight: 10 }}
            >
              <Text variant="xLarge">Accounts</Text>
              <HelpIcon appLinkType="LOOKUP_ACCOUNTS" />
            </Stack>
            <ChartAccount
              chartOfAccountId={chartOfAccount}
              onSelectOption={setChartOfAccount}
              setChartOfAccountLoading={setChartOfAccountLoading}
            />
            <CreateChartOfAccounts chartOfAccountId={chartOfAccount?.id!} />
            <EditChartAccounts chartOfAccountId={chartOfAccount?.id!} />
          </Stack>
          <Stack horizontal tokens={{ childrenGap: 10 }}>
            <ActionMessageModal
              entityType={EntityType.Account}
              disabled={!selectedRows.some((selected) => selected._isDeletable)}
              visible={selectedRows.length > 0}
              multiple={{
                validCount: selectedRows.filter(
                  (selected) => selected._isDeletable
                ).length,
                invalidNames: selectedRows
                  .filter((selected) => !selected._isDeletable)
                  .map((selected) => selected.lookupName || ''),
              }}
              onConfirm={_onConfirm}
            />
            <CorporateAccountAssignment
              chartOfAccount={chartOfAccount}
              onRefetch={() => refetch?.()}
            />
            <PrimaryButton
              text="New Account"
              iconProps={{ iconName: 'Add' }}
              onClick={() => setIsOpen(true)}
            />
          </Stack>
        </Stack>
        <SearchBar
          columns={[]}
          searchEnabled
          isGlobalAvailable={false}
          onEnterPress={(value) => setLookupName(value)}
          onRefresh={() => handleSearch(false)}
        />
      </Stack>
      <InfiniteList
        loading={chartOfAccountLoading || (!!chartOfAccount && isDataLoading)}
        onRenderItemColumn={_renderItemColumn}
        hasNextPage={pageInfo?.hasNextPage}
        items={transformedData}
        selectionMode={SelectionMode.multiple}
        onSelectionChanged={setSelectedRows}
        columns={gridColumns.filter((_column) => _column.isVisible)}
        onColumnHeaderClick={(_, column) => {
          if (column) _onColumnClick(column);
        }}
        onLoadMore={async () => await handleSearch(true)}
      />
      {isOpen && !!chartOfAccount && (
        <AccountsEntryModal
          chartOfAccount={chartOfAccount}
          onClose={() => setIsOpen(false)}
        />
      )}
    </>
  );
};
